// src/services/analytics/Analytics.ts
import { AnalyticsProvider, AnalyticsEventParams } from "./types";

class Analytics {
  private providers: AnalyticsProvider[] = [];
  private debug = false;

  constructor(options?: { debug?: boolean }) {
    this.debug = options?.debug ?? false;
  }

  addProvider(provider: AnalyticsProvider): this {
    provider.initialize();
    this.providers.push(provider);

    if (this.debug) {
      console.log(`[Analytics] Provider added: ${provider.name}`);
    }

    return this;
  }

  removeProvider(name: string): this {
    this.providers = this.providers.filter((p) => p.name !== name);
    return this;
  }

  pageView(pagePath: string, pageTitle: string): void {
    if (this.debug) {
      console.log(`[Analytics] pageView:`, { pagePath, pageTitle });
    }

    this.providers.forEach((provider) => {
      try {
        provider.pageView(pagePath, pageTitle);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} pageView error:`, error);
      }
    });
  }

  sendEvent(eventName: string, params?: AnalyticsEventParams): void {
    if (this.debug) {
      console.log(`[Analytics] sendEvent:`, { eventName, params });
    }

    this.providers.forEach((provider) => {
      try {
        provider.sendEvent(eventName, params);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} sendEvent error:`, error);
      }
    });
  }

  setUser(userId: string | null): void {
    if (this.debug) {
      console.log(`[Analytics] setUser:`, { userId });
    }

    this.providers.forEach((provider) => {
      try {
        provider.setUser(userId);
      } catch (error) {
        console.error(`[Analytics] ${provider.name} setUser error:`, error);
      }
    });
  }

  // Common event shortcuts
  trackFormSubmission(formId: string, formName: string): void {
    this.sendEvent("form_submission", { form_id: formId, form_name: formName });
  }

  trackFormCreated(formId: string, formName: string): void {
    this.sendEvent("form_created", { form_id: formId, form_name: formName });
  }

  trackSignUp(method?: string): void {
    this.sendEvent("sign_up", { method });
  }

  trackLogin(method?: string): void {
    this.sendEvent("login", { method });
  }

  trackError(errorType: string, errorMessage: string): void {
    this.sendEvent("error", {
      error_type: errorType,
      error_message: errorMessage,
    });
  }
}

export default Analytics;
